/**
 * Shared API-error formatting helper.
 *
 * Replaces the byte-identical `formatXxxError()` helpers that were inlined
 * across 6 admin pages during Phases 5.10h.1 through 5.10h.6:
 *  - SpecFilesPage        (5.10h.2)
 *  - ValidationRulesPage  (5.10h.1)
 *  - PromptLibraryPage    (5.10h.3)
 *  - FormTemplatesPage    (5.10h.4)
 *  - CompanyProfilePage   (5.10h.5)
 *  - UsersPage            (5.10h.6)
 *
 * Each consumer keeps its own per-page error-code map
 * (e.g. `SPEC_FILE_ERROR_MESSAGES`) and passes it in as `codeMap`. The
 * default empty map keeps the helper useful for callers that have no
 * friendly text to remap.
 *
 * Fallthrough order — matches the inline helpers verbatim so any
 * byte-identical migration is behavior-preserving:
 *  1. Lookup-map hit for `parsed.error` (when the backend body carries
 *     a bare code AND no message — e.g. `{ "error": "LastAdminProtection" }`).
 *  2. `parsed.message` (when the backend body carries an explicit
 *     message — most ValidationError shapes).
 *  3. `parsed.error` (bare code with no map entry — at least conveys
 *     the error name).
 *  4. `err.message` for any `ApiError` whose body didn't parse.
 *  5. `err.message` for any other `Error` (transport, network, etc.).
 *  6. `fallback` (last resort — never undefined).
 */

import { ApiError, parseApiErrorBody } from '@/api/v2Client';

/**
 * Phase phase-ui-consistency-sweep (§4.1) — never surface raw SQL or
 * internal database/driver errors to the user. A backend 500 from
 * knex/mssql carries the failed SQL statement in its message; without
 * this guard that leaked straight into the UI (e.g. the Settings
 * notification-preferences panel rendered
 * `select [Id], [Code], [Name] ... from [ValidationRules] ...`).
 *
 * When a candidate message looks like a SQL statement or a DB/driver
 * internal error, we discard it and fall back to the caller's friendly
 * message instead.
 */
const SQL_OR_DB_INTERNAL = [
  /\bselect\b[\s\S]*\bfrom\b/i, // select ... from ...
  /\binsert\s+into\b/i,
  /\bupdate\b[\s\S]*\bset\b/i,
  /\bdelete\s+from\b/i,
  /\bfrom\s+\[[A-Za-z0-9_]+\]/i, // from [TableName]
  /\b(RequestError|ConnectionError|mssql|tedious|knex|ECONNREFUSED|ETIMEDOUT)\b/i,
  /\bInvalid column name\b/i,
  /\bConversion failed\b/i,
  /\bIncorrect syntax near\b/i,
];

function looksLikeRawDbError(message: string): boolean {
  return SQL_OR_DB_INTERNAL.some((re) => re.test(message));
}

export { looksLikeRawDbError };

/**
 * T-17 D11 — the body's `error` / `message` are typed `string` but are not
 * guaranteed to be: `scan-learn.ts` once returned `error: { code, message }`,
 * which rendered as `[object Object]`. This reads both fields defensively and
 * only ever yields strings.
 */
export interface BodyText {
  code: string | null;
  message: string | null;
}

function nonEmptyString(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

export function extractBodyText(err: unknown): BodyText {
  const parsed = parseApiErrorBody(err) as unknown;
  if (parsed === null || typeof parsed !== 'object') return { code: null, message: null };
  const body = parsed as { error?: unknown; message?: unknown };
  let code = nonEmptyString(body.error);
  let message = nonEmptyString(body.message);
  if (body.error !== null && typeof body.error === 'object' && !Array.isArray(body.error)) {
    const nested = body.error as { code?: unknown; message?: unknown };
    code = nonEmptyString(nested.code);
    message = message ?? nonEmptyString(nested.message);
  }
  return { code, message };
}

export function formatApiError(
  err: unknown,
  fallback: string,
  codeMap: Readonly<Record<string, string>> = {},
): string {
  const { code, message } = extractBodyText(err);
  if (code && codeMap[code] && !message) {
    return codeMap[code]!;
  }
  const candidate =
    message ??
    code ??
    (err instanceof ApiError ? err.message : err instanceof Error ? err.message : null);
  if (candidate && !looksLikeRawDbError(candidate)) return candidate;
  return fallback;
}
